import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TrainingService } from '../../training.service';
import { BehaviorSubject, firstValueFrom, from, Observable, of, ReplaySubject, switchMap, tap } from 'rxjs';
import { orderBy } from 'natural-orderby';
import * as _ from 'underscore';

@Injectable({
  providedIn: 'root'
})
export class VolumesService {
  constructor(
    private httpClient: HttpClient,
    private trainingService: TrainingService
  ) {
  }

  volumesSubject = new ReplaySubject(1);
  volumeUuid = signal<string>('');
  elementsSubject = new ReplaySubject(1);

  async getApi(): Promise<any> {
    return await firstValueFrom(this.trainingService.load());
  }

  load(courseId: string): Observable<any> {
    return from(this.trainingService.loadByCourse(courseId)).pipe(
      switchMap((api: any) => {
        const url = api.getRoute('getVolumes');
        return this.httpClient.get(url).pipe(
          tap((volumes) => this.volumesSubject.next(volumes))
        );
      })
    );
  }

  triggerVolumeUpdate(courseId: string): void {
    this.load(courseId).subscribe()
  }

  async loadVolume(courseId: string, uuid: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.trainingService.loadByCourse(courseId).then((api) => {
        const url = api.getRoute('getVolumeUrl').replace('{webAppId}', courseId).replace('{uuid}', uuid);
        this.httpClient.get(url).subscribe((result) => {
          resolve(result);
        });
      });
    });
  }

  async getDocumentFromVolume(courseId: string, volumeUuid: string, documentUuId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.trainingService.loadByCourse(courseId).then((api) => {
        const url = api.getRoute('getVolumeDocument').replace('{webAppId}', courseId).replace('{uuid}', volumeUuid).replace('{documentUuId}', documentUuId);
        this.httpClient.get(url).subscribe((result) => {
          resolve(result);
        });
      });
    });
  }

  async getLessonFromVolume(courseId: string, volumeUuid: string, lessonUuId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.trainingService.loadByCourse(courseId).then((api) => {
        const url = api.getRoute('getVolumeLesson').replace('{webAppId}', courseId).replace('{uuid}', volumeUuid).replace('{lessonUuid}', lessonUuId);
        this.httpClient.get(url).subscribe((result) => {
          resolve(result);
        });
      });
    });
  }

  async getLessonsFromVolume(courseId: string, volumeUuid: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.trainingService.loadByCourse(courseId).then((api) => {
        const url = api.getRoute('getVolumeLessons').replace('{webAppId}', courseId).replace('{uuid}', volumeUuid);
        this.httpClient.get(url).subscribe((result) => {
          resolve(result);
        });
      });
    });
  }

  async getVideosFromVolume(courseId: string, volumeUuid: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.trainingService.loadByCourse(courseId).then((api) => {
        const url = api.getRoute('getVolumeVideos').replace('{webAppId}', courseId).replace('{uuid}', volumeUuid);
        this.httpClient.get(url).subscribe((result) => {
          resolve(result);
        });
      });
    });
  }

  getElementsFromVolume(courseId: string, volumeUuid: string): Observable<any> {
    this.volumeUuid.set(volumeUuid);

    return from(this.trainingService.loadByCourse(courseId)).pipe(
      switchMap((api: any) => {
        const url = api.getRoute('getVolumeElements').replace('{webAppId}', courseId).replace('{uuid}', volumeUuid);
        return this.httpClient.get(url).pipe(
          tap((elements) => this.elementsSubject.next(elements)))
      }));
  }

  triggerElementsUpdate(courseId: string): void {
    this.getElementsFromVolume(courseId, this.volumeUuid()).subscribe();
  }

  getVideoQuestionsFromVolume(courseType: string, volumeUuid: string): Observable<any> {
    return from(this.getApi()).pipe(
      switchMap(api =>
        this.httpClient.get(api.getRoute('getVideoQuestions').replace('{webAppId}', courseType).replace('{uuid}', volumeUuid)))
    );
  }

  async getQuizFromVolume(courseId: string, volumeUuid: string, quizUuid: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.trainingService.loadByCourse(courseId).then((api) => {
        const url = api.getRoute('getVolumeQuiz').replace('{webAppId}', courseId).replace('{uuid}', volumeUuid).replace('{volumeQuizUuid}', quizUuid);
        this.httpClient.get(url).subscribe((result: any) => {
          resolve(result);
        });
      });
    });
  }

  async getDocumentsFromVolume(courseId: string, volumeUuid: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.trainingService.loadByCourse(courseId).then((api) => {
        const url = api.getRoute('getVolumeDocuments').replace('{webAppId}', courseId).replace('{uuid}', volumeUuid);
        this.httpClient.get(url).subscribe((result) => {
          resolve(result);
        });
      });
    });
  }

  async getVolumeGroupCode(courseId: string, volumeUuid: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.trainingService.loadByCourse(courseId).then((api) => {
        const url = api.getRoute('getVolumeGroupCode').replace('{webAppId}', courseId).replace('{id}', volumeUuid);
        this.httpClient.get(url).subscribe((result) => {
          resolve(result);
        });
      });
    });
  }

  async loadGroups(courseId: string): Promise<any> {
    const api = await this.trainingService.loadByCourse(courseId);
    return new Promise((resolve, reject) => {
      this.httpClient.get(api.getRoute('volumeGroupsUrl')).subscribe({
        next: (v) => resolve(v),
        error: (e) => reject(e),
        complete: () => console.info('complete')
      }
      );
    });
  }

  public filter(value: any, volumeGroupId: string) {
    return orderBy(
      _.filter(value, element => {
        if (!volumeGroupId) {
          return true;
        } else if (volumeGroupId === 'VIDEO_TRAINING' && element.volumeGroupCode === null) {
          return true;
        } else if (element.volumeGroupCode === null) {
          return true;
        } else {
          return element.volumeGroupCode === volumeGroupId;
        }
      }),
      [v => v['sectionId']],
      ['asc']
    );
  }
}
